import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Divider,
  Grid,
  Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

import { getHedisPerformance } from "../../../../utils/services/CareGapsV2/careGapsV2Service";
import { getLocations } from "../../../../utils/services/control/masterDataService";

const pct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`);

const SummaryStat = ({ label, value, color = "text.primary" }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, minWidth: 140 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={800} color={color}>{value}</Typography>
  </Paper>
);

const PassChip = ({ passing }) => {
  if (passing === 1) return <Chip size="small" icon={<CheckCircleIcon />} label="Passing" color="success" />;
  if (passing === 0) return <Chip size="small" icon={<WarningIcon />} label="Below" color="error" />;
  return <Chip size="small" icon={<RemoveCircleIcon />} label="No peer rate" color="default" />;
};

export default function HedisPerformanceTab({ measurementYear }) {
  const [contractualOnly, setContractualOnly] = useState(true);
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState({ measures: [], summary: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getLocations({ status: 1, clinical: true })
      .then((arr) => setLocations(Array.isArray(arr) ? arr : []))
      .catch(() => setLocations([]));
  }, []);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHedisPerformance({
        measurementYear: measurementYear || undefined,
        locationId: locationId || undefined,
        contractualOnly,
      });
      setData({
        measures: Array.isArray(res?.measures) ? res.measures : [],
        summary: res?.summary || null,
      });
    } catch (e) {
      console.error("Error loading HEDIS performance:", e);
      setData({ measures: [], summary: null });
    } finally {
      setLoading(false);
    }
  }, [measurementYear, locationId, contractualOnly]);

  useEffect(() => { fetch(); }, [fetch]);

  const summary = data.summary;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Location</InputLabel>
            <Select
              label="Location"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <MenuItem value=""><em>Whole organization</em></MenuItem>
              {locations.map((l) => (
                <MenuItem key={l.locationId} value={l.locationId}>{l.medicalCenter}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={contractualOnly}
                onChange={(e) => setContractualOnly(e.target.checked)}
              />
            }
            label="Contractual QMs only (7)"
          />

          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Year {measurementYear}
          </Typography>
        </Stack>
      </Paper>

      {summary && (
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 6, md: 2 }}><SummaryStat label="Opportunities" value={(summary.totalOpportunities || 0).toLocaleString()} /></Grid>
            <Grid size={{ xs: 6, md: 2 }}><SummaryStat label="Open" value={(summary.totalOpen || 0).toLocaleString()} color="error.main" /></Grid>
            <Grid size={{ xs: 6, md: 2 }}><SummaryStat label="Submitted" value={(summary.totalSubmitted || 0).toLocaleString()} color="warning.main" /></Grid>
            <Grid size={{ xs: 6, md: 2 }}><SummaryStat label="Closed" value={(summary.totalClosed || 0).toLocaleString()} color="success.main" /></Grid>
            <Grid size={{ xs: 6, md: 2 }}><SummaryStat label="Global rate" value={pct(summary.globalRate)} /></Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <SummaryStat
                label="Quality gate"
                value={`${summary.qmsPassing}/${summary.qmsWithPeerRate}`}
                color={summary.qmsPassing >= summary.qmsWithPeerRate ? "success.main" : "warning.main"}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : data.measures.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">No measures match the current filters.</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 800, bgcolor: "#607d8b", color: "#fff" } }}>
                  <TableCell>QM</TableCell>
                  <TableCell>Measure</TableCell>
                  <TableCell>Cat.</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Open</TableCell>
                  <TableCell align="right">Submitted</TableCell>
                  <TableCell align="right">Closed</TableCell>
                  <TableCell align="right">Internal rate</TableCell>
                  <TableCell align="right">Peer rate</TableCell>
                  <TableCell align="right">Gap</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.measures.map((m) => {
                  const internal = Number(m.internalRate || 0);
                  const peer = m.peerRate != null ? Number(m.peerRate) : null;
                  const ratePct = Math.min(100, Math.round(internal * 100));
                  return (
                    <TableRow
                      key={m.hedisMeasureId}
                      hover
                      sx={{
                        "& td": { whiteSpace: "nowrap" },
                        bgcolor: m.passingPeerRate === 1
                          ? "rgba(76,175,80,0.06)"
                          : m.passingPeerRate === 0
                            ? "rgba(244,67,54,0.05)"
                            : "transparent",
                      }}
                    >
                      <TableCell>
                        {m.qmCode ? (
                          <Chip size="small" label={m.qmCode} sx={{ bgcolor: "#e3f2fd", color: "#0d47a1", fontWeight: 700 }} />
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={m.habilityName || ""}>
                          <Typography variant="body2" fontWeight={600}>
                            {m.bcbsName || m.habilityName}
                          </Typography>
                        </Tooltip>
                        {m.inverted ? (
                          <Typography variant="caption" color="text.secondary">↓ inverted (lower is better)</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{m.category || "—"}</TableCell>
                      <TableCell align="right">{(m.total || 0).toLocaleString()}</TableCell>
                      <TableCell align="right">{(m.open || 0).toLocaleString()}</TableCell>
                      <TableCell align="right">{(m.submitted || 0).toLocaleString()}</TableCell>
                      <TableCell align="right">{(m.closed || 0).toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ minWidth: 100 }}>
                          <Typography variant="body2" fontWeight={700}>{pct(internal)}</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={ratePct}
                            color={m.passingPeerRate === 1 ? "success" : m.passingPeerRate === 0 ? "error" : "info"}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">{peer != null ? pct(peer) : "—"}</TableCell>
                      <TableCell align="right">
                        {m.gapToPeerRate == null ? "—" : (
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={m.gapToPeerRate <= 0 ? "success.main" : "error.main"}
                          >
                            {m.gapToPeerRate <= 0 ? `${m.gapToPeerRate}` : `+${m.gapToPeerRate}`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <PassChip passing={m.passingPeerRate} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Peer rates: BCBS Florida monthly report. <strong>Gap</strong> = how many more care gaps must be closed to reach the peer rate (negative = already exceeded).
        </Typography>
      </Box>
    </Box>
  );
}
